import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Sparkles, Lock, Zap } from "lucide-react";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    if (url.startsWith("/api/photos/")) return photoUrl(url.replace("/api/photos/", ""));
    return `${BACKEND_URL}${url}`;
}

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

/** Displays a blurred silhouette card for free users. */
function BlurredCard({ profile, idx }) {
    const gradients = [
        "from-primary/40 via-fuchsia-400/30 to-orange-300/40",
        "from-emerald-400/40 via-cyan-400/30 to-primary/40",
        "from-yellow-300/40 via-primary/30 to-rose-400/40",
        "from-primary/40 via-purple-400/30 to-emerald-300/40",
    ];
    const g = gradients[idx % gradients.length];
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
            data-testid={`liked-blurred-${idx}`}
            className={`relative overflow-hidden rounded-3xl border-2 border-foreground aspect-[3/4] bg-gradient-to-br ${g}`}
        >
            {/* silhouette */}
            <div className="absolute inset-0 grid place-items-center">
                <div className="h-16 w-16 rounded-full bg-white/40 backdrop-blur-md grid place-items-center text-3xl font-black text-white/90">
                    {profile.name_initial || "?"}
                </div>
            </div>
            {/* frost overlay */}
            <div className="absolute inset-0 backdrop-blur-2xl bg-white/10" />
            {/* re-render initial on top of frost */}
            <div className="absolute inset-0 grid place-items-center">
                <div className="h-16 w-16 rounded-full bg-white/30 backdrop-blur-md grid place-items-center text-3xl font-black text-white/90">
                    {profile.name_initial || "?"}
                </div>
            </div>
            {/* bottom info */}
            <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/60 to-transparent text-white">
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                    <span>{profile.city || "•"}</span>
                    <span className="rounded-full bg-primary px-2 py-0.5">
                        {profile.match_percent ?? 0}%
                    </span>
                </div>
                {profile.age_band && (
                    <div className="mt-1 text-xs font-bold opacity-90">age {profile.age_band}</div>
                )}
            </div>
            {/* Lock badge */}
            <div className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/30 backdrop-blur-md grid place-items-center">
                <Lock className="h-4 w-4 text-white" strokeWidth={2.8} />
            </div>
        </motion.div>
    );
}

function UnlockedCard({ profile, idx, onLikeBack, liking }) {
    const avatar =
        profile.picture ||
        (profile.photo_urls?.[0] ? toAbsolute(profile.photo_urls[0]) : null);
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
            data-testid={`liked-revealed-${idx}`}
            className="relative overflow-hidden rounded-3xl border-2 border-foreground aspect-[3/4] bg-card"
        >
            {avatar ? (
                <img src={avatar} alt={profile.name} className="absolute inset-0 h-full w-full object-cover" />
            ) : (
                <div className="absolute inset-0 grid place-items-center bg-accent text-accent-foreground text-5xl font-black">
                    {(profile.name || "?").charAt(0).toUpperCase()}
                </div>
            )}
            <div className="absolute top-2 right-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-[10px] font-black">
                {profile.match_percent ?? 0}%
            </div>
            <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white">
                <div className="font-display font-black text-lg leading-tight">
                    {profile.name}{profile.age ? `, ${profile.age}` : ""}
                </div>
                <div className="text-[11px] opacity-90">
                    {profile.city} · {timeAgo(profile.liked_at)}
                </div>
                <button
                    onClick={() => onLikeBack(profile)}
                    disabled={liking}
                    data-testid={`liked-back-btn-${idx}`}
                    className="mt-2 h-9 w-full rounded-full bg-primary text-primary-foreground text-xs font-black uppercase tracking-widest lift disabled:opacity-50 flex items-center justify-center gap-1"
                >
                    <Heart className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                    Like back
                </button>
            </div>
        </motion.div>
    );
}

export default function WhoLikedYouSheet({ open, onClose }) {
    const { user } = useAuth();
    const { openPaywall } = usePaywall();
    const [loading, setLoading] = useState(true);
    const [count, setCount] = useState(0);
    const [premium, setPremium] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [likingId, setLikingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/likes/received");
            setCount(data.count || 0);
            setPremium(!!data.premium);
            setProfiles(data.profiles || []);
        } catch (e) {
            toast.error("Couldn't load your admirers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
    }, [open]);

    const likeBack = async (profile) => {
        if (likingId) return;
        setLikingId(profile.user_id);
        try {
            const { data } = await api.post("/swipe", {
                target_user_id: profile.user_id,
                direction: "like",
            });
            setProfiles((p) => p.filter((x) => x.user_id !== profile.user_id));
            setCount((c) => Math.max(0, c - 1));
            if (data.matched) {
                toast.success(`It's a match with ${profile.name}! 🎉`, {
                    description: "Slide into their DMs from the Chats tab",
                });
            } else {
                toast(`Liked ${profile.name} back 💜`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Couldn't like back");
        } finally {
            setLikingId(null);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    data-testid="who-liked-sheet"
                    className="fixed inset-0 z-[70] bg-background/70 backdrop-blur-xl"
                >
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl border-t-2 border-foreground bg-card p-5 pb-8 max-h-[90vh] flex flex-col"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4 flex-shrink-0">
                            <div>
                                <div className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1">
                                    <Sparkles className="h-3 w-3" strokeWidth={3} /> vibe check
                                </div>
                                <div className="font-display text-2xl font-black leading-tight">
                                    {count > 0
                                        ? `${count} ${count === 1 ? "person" : "people"} liked you 👀`
                                        : "No likes yet — post more vibes ✨"}
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                data-testid="who-liked-close-btn"
                                className="h-9 w-9 rounded-full hover:bg-accent grid place-items-center flex-shrink-0"
                            >
                                <X className="h-5 w-5" strokeWidth={2.5} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="overflow-y-auto flex-1 -mx-1 px-1">
                            {loading ? (
                                <div className="text-center text-sm text-muted-foreground py-10">
                                    Loading your admirers…
                                </div>
                            ) : profiles.length === 0 ? (
                                <div className="text-center py-10 text-sm text-muted-foreground" data-testid="who-liked-empty">
                                    <div className="text-4xl mb-3">🌱</div>
                                    Your first fans are on the way. Keep swiping and updating your vibe.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    {profiles.map((p, i) =>
                                        premium ? (
                                            <UnlockedCard
                                                key={p.user_id}
                                                profile={p}
                                                idx={i}
                                                onLikeBack={likeBack}
                                                liking={likingId === p.user_id}
                                            />
                                        ) : (
                                            <BlurredCard key={p.user_id} profile={p} idx={i} />
                                        ),
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Premium CTA */}
                        {!premium && count > 0 && !loading && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-4 flex-shrink-0 rounded-3xl border-2 border-foreground bg-primary text-primary-foreground p-4 relative overflow-hidden"
                                data-testid="who-liked-premium-cta"
                            >
                                <div className="relative flex items-center gap-3">
                                    <div className="h-11 w-11 rounded-2xl bg-white/20 grid place-items-center flex-shrink-0">
                                        <Zap className="h-5 w-5" fill="currentColor" strokeWidth={0} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-black text-base leading-tight">
                                            Reveal all {count} {count === 1 ? "admirer" : "admirers"}
                                        </div>
                                        <div className="text-xs opacity-90">
                                            Skip the swipe queue. Match instantly with people who already picked you.
                                        </div>
                                    </div>
                                    <Button
                                        onClick={() => {
                                            onClose();
                                            openPaywall({
                                                reason: "generic",
                                                message: `Unlock ${count} people who liked you 👀`,
                                            });
                                        }}
                                        data-testid="who-liked-unlock-btn"
                                        className="h-11 rounded-2xl bg-white text-primary font-black hover:bg-white/90 flex-shrink-0"
                                    >
                                        Unlock
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
